import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia
} from '@mui/material';
import { Add, Edit, Delete, Search, PlayArrow, Image, VideoLibrary, Visibility, NotificationsActive, Refresh, FilterList, People, Link as LinkIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { storyService } from '../services/api';
import StoryForm from '../components/StoryForm';
import SendNotificationModal from '../components/notifications/SendNotificationModal';
import ViewersModal from '../components/views/ViewersModal';

const Stories = () => {
  const { t } = useTranslation();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewStory, setPreviewStory] = useState(null);
  const [notificationModal, setNotificationModal] = useState({ open: false, story: null });
  const [showInactive, setShowInactive] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [storyToReactivate, setStoryToReactivate] = useState(null);
  const [viewersModal, setViewersModal] = useState({ open: false, story: null });

  useEffect(() => {
    fetchStories();
  }, [currentPage, rowsPerPage, showInactive]);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        includeUnpublished: true // Admin should see all stories including drafts
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await storyService.getAll(params);
      setStories(response.docs || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStories();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const handleDelete = async (id) => {
    if (window.confirm(t('stories.confirmDelete'))) {
      try {
        await storyService.delete(id);
        fetchStories();
      } catch (error) {
        console.error('Error deleting story:', error);
      }
    }
  };

  const handleReactivate = async () => {
    if (!storyToReactivate) return;

    try {
      await storyService.reactivate(storyToReactivate._id);
      setReactivateDialogOpen(false);
      setStoryToReactivate(null);
      fetchStories();
    } catch (error) {
      console.error('Error reactivating story:', error);
      alert(t('stories.errorReactivating') || 'Error reactivating story');
    }
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1); // TablePagination uses 0-based index
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const isExpired = (expiresAt) => {
    return new Date() > new Date(expiresAt);
  };

  const getStatusColor = (story) => {
    if (!story.isActive) return 'error';
    if (isExpired(story.expiresAt)) return 'error';
    return story.isPublished ? 'success' : 'warning';
  };

  const getStatusLabel = (story) => {
    if (!story.isActive) return t('stories.inactive') || 'Inactive';
    if (isExpired(story.expiresAt)) return t('stories.expired') || 'Expired';
    return story.isPublished ? t('stories.published') : t('stories.draft') || 'Draft';
  };

  const formatDuration = (duration) => {
    return `${duration}s`;
  };

  const getThumbnailUrl = (story) => {
    // Priority: Custom thumbnail > Story URL for images > null for videos without thumbnail
    if (story.thumbnailUrl) {
      return story.thumbnailUrl;
    }
    // For image stories, use the story URL itself as thumbnail
    if (story.mediaType === 'image' && story.storyUrl) {
      return story.storyUrl;
    }
    // For videos without thumbnail, return null (will show default icon)
    return null;
  };

  const getStoryUrl = (story) => {
    return story.storyUrl || null;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t('stories.title')}
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant={showInactive ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setShowInactive(!showInactive)}
            color={showInactive ? 'secondary' : 'primary'}
          >
            {showInactive ? t('stories.hideInactive') || 'Hide Inactive' : t('stories.showInactive') || 'Show Inactive'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setSelectedStory(null);
              setFormOpen(true);
            }}
          >
            {t('stories.add')}
          </Button>
        </Box>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        placeholder={t('stories.searchPlaceholder')}
        value={search}
        onChange={handleSearch}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {/* Stories Table */}
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 100 }}>{t('stories.thumbnail')}</TableCell>
              <TableCell>{t('stories.titleField')}</TableCell>
              <TableCell sx={{ width: 200 }}>{t('stories.category')}</TableCell>
              <TableCell sx={{ width: 100 }}>{t('stories.mediaType')}</TableCell>
              <TableCell sx={{ width: 100 }}>{t('stories.duration')}</TableCell>
              <TableCell sx={{ width: 120 }}>{t('common.status')}</TableCell>
              <TableCell sx={{ width: 140 }}>{t('stories.expiresAt')}</TableCell>
              <TableCell sx={{ width: 150 }}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    {t('common.loading')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : stories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    {t('stories.noStoriesFound')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              stories.map((story) => (
                <TableRow key={story._id} hover>
                  <TableCell>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <Avatar
                        src={getThumbnailUrl(story)}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                        variant="rounded"
                        onClick={() => {
                          setPreviewStory(story);
                          setPreviewOpen(true);
                        }}
                      >
                        {story.mediaType === 'videoShare'
                          ? <LinkIcon />
                          : story.mediaType === 'video'
                            ? <VideoLibrary />
                            : <Image />}
                      </Avatar>
                      {story.mediaType === 'video' && (
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            width: 20,
                            height: 20,
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.8)'
                            }
                          }}
                          onClick={() => {
                            setPreviewStory(story);
                            setPreviewOpen(true);
                          }}
                        >
                          <PlayArrow sx={{ fontSize: 12 }} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          backgroundColor: 'rgba(255,255,255,0.8)',
                          width: 18,
                          height: 18,
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.9)'
                          }
                        }}
                        onClick={() => {
                          setPreviewStory(story);
                          setPreviewOpen(true);
                        }}
                      >
                        <Visibility sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {story.titleGr || story.titleEn || story.title || 'Untitled'}
                    </Typography>
                    {story.descriptionGr && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {story.descriptionGr.length > 50
                          ? `${story.descriptionGr.substring(0, 50)}...`
                          : story.descriptionGr}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {story.category?.nameGr || story.category?.nameEn || t('videos.noCategory')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {story.mediaType === 'videoShare'
                        ? <LinkIcon fontSize="small" />
                        : story.mediaType === 'video'
                          ? <VideoLibrary fontSize="small" />
                          : <Image fontSize="small" />}
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {t(`stories.${story.mediaType}`)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDuration(story.duration)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(story)}
                      color={getStatusColor(story)}
                      size="small"
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={isExpired(story.expiresAt) ? 'error' : 'text.secondary'}>
                      {new Date(story.expiresAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => setViewersModal({ open: true, story })}
                        sx={{ color: 'info.main' }}
                        title={t('viewers.viewersButton')}
                      >
                        <People fontSize="small" />
                      </IconButton>
                      {!story.isActive ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setStoryToReactivate(story);
                            setReactivateDialogOpen(true);
                          }}
                          sx={{ color: 'success.main' }}
                          title={t('stories.reactivate') || 'Reactivate'}
                        >
                          <Refresh fontSize="small" />
                        </IconButton>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedStory(story);
                              setFormOpen(true);
                            }}
                            sx={{ color: 'primary.main' }}
                            title={t('common.edit')}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setNotificationModal({ open: true, story })}
                            sx={{ color: 'info.main' }}
                            title={t('notifications.sendNotification')}
                          >
                            <NotificationsActive fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(story._id)}
                        sx={{ color: 'error.main' }}
                        title={t('common.delete')}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Table Pagination */}
      <TablePagination
        component="div"
        count={totalPages * rowsPerPage} // Approximate total based on pages
        page={currentPage - 1} // TablePagination uses 0-based index
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelDisplayedRows={({ from, to }) =>
          `${from}-${to} ${t('common.of')} ${t('common.page')} ${currentPage}/${totalPages}`
        }
        labelRowsPerPage={t('common.rowsPerPage')}
      />

      <StoryForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        story={selectedStory}
        onSuccess={fetchStories}
      />

      {/* Story Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {previewStory?.titleGr || previewStory?.titleEn || previewStory?.title || t('stories.preview')}
        </DialogTitle>
        <DialogContent>
          {previewStory && (
            <Card sx={{ maxWidth: '100%', mx: 'auto' }}>
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component={previewStory.mediaType === 'video' ? 'video' : 'img'}
                  // A videoShare story has no media of its own, so preview its
                  // thumbnail (the linked video's) rather than an empty source.
                  src={
                    previewStory.mediaType === 'videoShare'
                      ? getThumbnailUrl(previewStory)
                      : getStoryUrl(previewStory)
                  }
                  controls={previewStory.mediaType === 'video'}
                  autoPlay={previewStory.mediaType === 'video'}
                  muted={previewStory.mediaType === 'video'}
                  sx={{
                    height: 400,
                    width: '100%',
                    objectFit: 'contain',
                    backgroundColor: 'black'
                  }}
                />
                {previewStory.mediaType === 'video' && (
                  <Chip
                    icon={<PlayArrow />}
                    label={`${previewStory.duration}s`}
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8
                    }}
                  />
                )}
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('stories.category')}: {previewStory.category?.nameGr || previewStory.category?.nameEn}
                </Typography>
                {previewStory.descriptionGr && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {previewStory.descriptionGr}
                  </Typography>
                )}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    icon={previewStory.mediaType === 'video' ? <VideoLibrary /> : <Image />}
                    label={t(`stories.${previewStory.mediaType}`)}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('stories.expiresAt')}: {new Date(previewStory.expiresAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            {t('common.close')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setPreviewOpen(false);
              setSelectedStory(previewStory);
              setFormOpen(true);
            }}
            startIcon={<Edit />}
          >
            {t('stories.edit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Modal */}
      {notificationModal.story && (
        <SendNotificationModal
          open={notificationModal.open}
          onClose={() => setNotificationModal({ open: false, story: null })}
          contentType="story"
          contentId={notificationModal.story._id}
          prefilledTitleGr={notificationModal.story.titleGr || notificationModal.story.title || ''}
          prefilledTitleEn={notificationModal.story.titleEn || ''}
          prefilledBodyGr="Νέα ιστορία διαθέσιμη"
          prefilledBodyEn="New story available"
        />
      )}

      {/* Viewers Modal */}
      {viewersModal.story && (
        <ViewersModal
          open={viewersModal.open}
          onClose={() => setViewersModal({ open: false, story: null })}
          contentType="story"
          contentId={viewersModal.story._id}
          contentTitle={viewersModal.story.titleGr || viewersModal.story.titleEn || viewersModal.story.title}
        />
      )}

      {/* Reactivate Story Confirmation Dialog */}
      <Dialog
        open={reactivateDialogOpen}
        onClose={() => setReactivateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('stories.reactivateStory') || 'Reactivate Story'}
        </DialogTitle>
        <DialogContent>
          {storyToReactivate && (
            <Box>
              <Typography variant="body1" gutterBottom>
                {t('stories.confirmReactivate') || 'Are you sure you want to reactivate this story?'}
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {storyToReactivate.titleGr || storyToReactivate.titleEn || 'Untitled'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {t('stories.originalExpiration') || 'Original expiration'}: {new Date(storyToReactivate.expiresAt).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                  {t('stories.newExpiration') || 'New expiration'}: {new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleReactivate}
            startIcon={<Refresh />}
            color="success"
          >
            {t('stories.reactivate') || 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Stories;